import { useEffect, useMemo, useState } from 'react';
import { collection, deleteDoc, doc, onSnapshot, serverTimestamp, setDoc } from 'firebase/firestore';
import { firestore } from '../firebase';
import type { ChatItem, ChatReaction } from '../types';
import { getChatCollectionName } from './useProfile';

/**
 * Reactions live in a flat `{collection}/{chatId}/reactions` collection
 * (doc id `{messageId}_{uid}`, one reaction per user per message) rather
 * than as a field on the message doc — every chat message subcollection is
 * `allow update, delete: if false` (immutable by design), so a reaction
 * can never be written onto the message itself. One listener per open
 * chat, grouped client-side by messageId.
 */
export function useChatReactions(chatId: string | null, kind: ChatItem['kind']) {
  const [reactions, setReactions] = useState<ChatReaction[]>([]);

  useEffect(() => {
    if (!chatId) {
      setReactions([]);
      return undefined;
    }
    const collectionName = getChatCollectionName(kind);
    const unsub = onSnapshot(
      collection(firestore, collectionName, chatId, 'reactions'),
      snapshot => {
        setReactions(snapshot.docs.map(d => {
          const raw = d.data();
          return {
            id: d.id,
            messageId: String(raw.messageId ?? ''),
            uid: String(raw.uid ?? ''),
            emoji: String(raw.emoji ?? ''),
            createdAt: raw.createdAt,
          };
        }));
      },
      error => {
        console.warn('[Tonyte][RN][ChatReactions] subscription failed', { chatId, kind, error });
      },
    );
    return unsub;
  }, [chatId, kind]);

  const reactionsByMessageId = useMemo(() => {
    const grouped = new Map<string, ChatReaction[]>();
    reactions.forEach(reaction => {
      const list = grouped.get(reaction.messageId);
      if (list) list.push(reaction);
      else grouped.set(reaction.messageId, [reaction]);
    });
    return grouped;
  }, [reactions]);

  return { reactionsByMessageId };
}

function reactionDocId(messageId: string, uid: string): string {
  return `${messageId}_${uid}`;
}

/** Tap the same emoji again to remove it; tap a different one to switch. */
export async function toggleMessageReaction({
  chatId,
  kind,
  messageId,
  uid,
  emoji,
  currentEmoji,
}: {
  chatId: string;
  kind: ChatItem['kind'];
  messageId: string;
  uid: string;
  emoji: string;
  currentEmoji?: string;
}): Promise<void> {
  const collectionName = getChatCollectionName(kind);
  const ref = doc(firestore, collectionName, chatId, 'reactions', reactionDocId(messageId, uid));

  if (currentEmoji === emoji) {
    await deleteDoc(ref);
    return;
  }
  await setDoc(ref, { messageId, uid, emoji, createdAt: serverTimestamp() });
}
